import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import SearchIcon from "@styles/all-images/icons/search.svg";
import ArrowDownIcon from "@styles/all-images/icons/chevron-down-grey.svg";
import LiveChatIcon from "@styles/all-images/icons/livechatblue.svg";
import PhoneIcon from "@styles/all-images/icons/phoneblue.svg";
import EmailIcon from "@styles/all-images/icons/emailblue.svg";
import ViewTermsSheetModal from "@/pages/TierConfiguration/ViewTermsSheetModal";
import TierConfigurationRejectionReason from "@/pages/TierConfiguration/TierConfigurationRejectionReason";
import actionsHandler from "@/store/actions/actionHandler";
import { toast } from "react-toastify";
import { AppThunkDispatch, RootState } from "@/store";
import { S3Image } from "@/utils/s3-helper";
import CircularProgress from "@mui/material/CircularProgress";
import StandaloneChatDrawer, {
  StandaloneChatDrawerType,
} from "../LiveChat/StandaloneChatDrawer";
import { CreateGroupResponse } from "../LiveChat/types/api-response-types";
import CustomLoader from "@/components/common/loader/SpinnerLoader";
import { debounce } from "lodash";
import debounceHandler from "@/composables/debounceHandler";

interface UserType {
  id: number;
  user_type: string;
}

interface Contact {
  id: string;
  title: string;
  items: IndividualContactItem[];
}

interface IndividualContactItem {
  id: number;
  name: string;
  role: string;
  phone: string;
  email: string;
  logo: string;
  room_id?: number | null;
}

const TierConfigurationModule = () => {
  const [isViewTermsSheetModalOpen, setIsViewTermsSheetModalOpen] =
    useState(false);
  const [expandedSection, setExpandedSection] = useState("T1");
  const [searchTerm, setSearchTerm] = useState("");
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [activeTier, setActiveTier] = useState(1);
  const dispatch = useDispatch<AppThunkDispatch>();
  const [userTypes, setUserTypes] = useState<UserType[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const userType = useSelector(
    (state: RootState) => state.auth.user?.user_type.id,
  );
  const [individualChatUserId, setIndividualChatUserId] = useState<
    number | null
  >(null);
  const [individualChatRoomId, setIndividualChatRoomId] = useState<
    string | null
  >(null);
  const [isIndividualChatOpen, setIsIndividualChatOpen] = useState(false);
  const [isIndividualChatCreationLoading, setIsIndividualChatCreationLoading] =
    useState<{ [key: number]: boolean }>({});
  const debouncedScrollRef = useRef(null);
  const { debouncedCallback } = debounceHandler();

  const handleCloseTermsSheet = () => {
    setIsViewTermsSheetModalOpen(false); // Fixed: Close the terms sheet modal correctly
  };

  const handleCloseRejectionModal = () => {
    setIsRejectionModalOpen(false);
  };

  const handleRejectionSubmit = () => {
    setIsRejectionModalOpen(false);
  };

  const handleCreatingIndividualChatRoom = (
    userId: number,
  ): Promise<CreateGroupResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        setIsIndividualChatCreationLoading((prev) => ({
          ...prev,
          [userId]: true,
        }));
        const { authenticatedCall } = actionsHandler("createIndividualChat");
        const response = await dispatch(
          authenticatedCall({
            method: "post",
            payload: {
              recipient_id: userId,
            },
          }),
        );

        if (response.ok) {
          resolve(response.data as CreateGroupResponse);
        }
      } catch (error) {
        console.error("Error creating individual chat:", error);
        reject(error);
      } finally {
        setIsIndividualChatCreationLoading((prev) => ({
          ...prev,
          [userId]: false,
        }));
      }
    });
  };

  const handleIndividualChat = async (item: IndividualContactItem) => {
    if (item.room_id) {
      setIndividualChatRoomId(item.room_id?.toString() || null);
    } else {
      const data = await handleCreatingIndividualChatRoom(item.id);
      setIndividualChatRoomId(data.room_id.toString() || null);
    }
    setIndividualChatUserId(item.id);
    setIsIndividualChatOpen(true);
  };

  useEffect(() => {
    if (!isIndividualChatOpen) {
      setIndividualChatRoomId(null);
      setIndividualChatUserId(null);
    }
  }, [isIndividualChatOpen]);

  useEffect(() => {
    const fetchUserTypes = async () => {
      try {
        const { authenticatedCall } = actionsHandler("userTypeList");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
          }),
        );

        if (response.ok) {
          // Define accessible tiers based on user type
          const getTierAccess = (currentUserType: number) => {
            const tierAccess: { [key: number]: number[] } = {
              1: [1, 3], // T1 can view T1 and T3
              2: [2, 3], // T2 can view T2 and T3
              3: [1, 2, 3, 4, 5, 6, 7], // T3 can view all
              4: [3, 4, 5, 7], // T4 can view T3, T4, T5 and T7
              5: [4, 5], // T5 can view T4 and T5
              6: [3, 6], // T6 can view T3 and T6
              7: [3, 4, 7], // T7 can view T3, T4, and T7
            };
            return tierAccess[currentUserType] || [];
          };

          const accessibleTiers = getTierAccess(userType);
          setUserTypes(
            response.data.filter((type: UserType) =>
              accessibleTiers.includes(type.id),
            ),
          );
        } else {
          toast.error(response.data?.message || "Failed to fetch user types");
        }
      } catch (error) {
        console.error("Error fetching user types:", error);
      }
    };

    fetchUserTypes();
  }, [dispatch, userType]);

  const debouncedFetchContacts = useCallback(
    async (searchValue: string) => {
      if (!userTypes[activeTier - 1]?.id) return;

      setIsLoading(true);
      try {
        const { authenticatedCall } = actionsHandler("contactsList");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customQuery: `user_type=${userTypes[activeTier - 1]?.id}${searchValue ? `&search=${searchValue}` : ""}`,
          }),
        );

        if (response.ok) {
          // Ensure we're working with an array
          const companiesData = Array.isArray(response.data)
            ? response.data
            : [];

          // Transform and filter out empty companies
          const transformedContacts = companiesData
            .filter(
              (company: any) =>
                company && company.users && company.users.length > 0,
            )
            .map((company: any) => ({
              id: `T${company.id}`,
              title: company.name || "Unnamed Company",
              items: company.users
                .filter((user: any) => user && user.id) // Filter out invalid users
                .map((user: any) => ({
                  id: user.id,
                  name:
                    user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : "Unnamed User",
                  role:
                    user.permission_profiles
                      ?.flatMap((profile: any) => profile.name)
                      .filter(Boolean)
                      .join(", ") || "Not Available",
                  phone: user.phone || "N/A",
                  email: user.email || "N/A",
                  logo: user.profile_picture || "",
                  room_id: user.room_id || null,
                })),
            }))
            .filter((company: Contact) => company.items.length > 0); // Remove companies with no valid users

          setContacts(transformedContacts);
        } else {
          setContacts([]);
          toast.error(response.data?.message || "Failed to fetch contacts");
        }
      } catch (error) {
        console.error("Error fetching contacts:", error);
        setContacts([]);
        toast.error("Failed to fetch contacts");
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, userTypes, activeTier],
  );

  // Handle search input change
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  // Remove search dependency from useEffect
  useEffect(() => {
    if (userTypes[activeTier - 1]?.id) {
      debouncedCallback({
        debounceRef: debouncedScrollRef,
        callback: async () => {
          debouncedFetchContacts(searchTerm);
        },
      });
    }
  }, [activeTier, userTypes, searchTerm]);

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-pt-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <a href="/">
                <img src={BackIcon} alt="Back Icon" />
              </a>
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Contacts
              </h1>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-mt-4">
            {userTypes.map((type, index) => (
              <button
                key={type.id}
                className={`tw-border tw-px-4 tw-py-2 
                  ${activeTier === index + 1 ? "tw-text-[#146C94] tw-border-b-4 tw-border-b-[#146C94]" : "tw-border-b-[#ffffff]"}
                  focus:tw-border--b-[#146C94] tw-border-b-4`}
                onClick={() => setActiveTier(index + 1)}
              >
                {type.user_type}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="tw-p-6 tw-mt-[197px] tw-flex-col tw-flex">
        <div className="tw-relative">
          <input
            type="text"
            placeholder="Search company name or user name"
            className="tw-pl-10 tw-pr-4 tw-py-2 tw-border tw-border-[#E9EAEB] tw-shadow tw-rounded-lg tw-w-full lg:tw-w-1/4"
            value={searchTerm}
            onChange={handleSearch}
          />
          <img
            src={SearchIcon}
            alt="Search Icon"
            className="tw-absolute tw-left-3 tw-top-2.5 tw-h-5 tw-w-5 tw-text-gray-400"
          />
        </div>
        <div>
          <div>
            <div className="tw-full">
              {isLoading ? (
                <div className="tw-flex tw-justify-center tw-items-center">
                  <CircularProgress />
                </div>
              ) : (
                <>
                  {contacts.length > 0 ? (
                    contacts.map((tier) => (
                      <div
                        key={tier.id}
                        className="tw-rounded-lg tw-overflow-hidden tw-bg-[#FFFFFF] tw-border tw-border-[#E9EAEB]  tw-shadow-sm tw-my-3"
                      >
                        <button
                          onClick={() =>
                            setExpandedSection(
                              expandedSection === tier.id ? "" : tier.id,
                            )
                          }
                          className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-p-4 tw-text-left tw-transition-colors 
                            ${expandedSection === tier.id ? "tw-bg-[#ECF1F8] tw-text-[#232323]" : ""}`}
                        >
                          <span className="tw-font-medium">{tier.title}</span>
                          <img src={ArrowDownIcon} alt="Arrow Down Icon" />
                        </button>

                        {expandedSection === tier.id && (
                          <div className="tw-p-4 tw-bg-white tw-border-t">
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                              {tier.items.map((item, index) => (
                                <div
                                  key={index}
                                  className="tw-px-4 tw-py-3 tw-bg-white tw-rounded-lg tw-border tw-border-[#E9EAEB]"
                                >
                                  <div className="tw-flex tw-justify-between tw-gap-2">
                                    <div className="tw-flex tw-gap-4">
                                      <S3Image
                                        src={item.logo} // Use logo from the item object
                                        alt="logo"
                                        className="tw-w-10 tw-h-10 tw-rounded-full"
                                        cacheKey={item.logo}
                                      />
                                      <div>
                                        <h6 className="tw-text-[#232323] tw-text-ebase tw-font-semibold tw-mb-1">
                                          {item.name}
                                        </h6>
                                        <p className="tw-text-[#475569] tw-text-sm tw-font-normal tw-mb-3">
                                          {item.role}
                                        </p>
                                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                                          <a
                                            href={`tel:${item.phone}`}
                                            className="tw-flex tw-items-center tw-gap-2"
                                          >
                                            <img
                                              src={PhoneIcon}
                                              alt=""
                                              className="tw-w-6 tw-h-6 tw-min-w-6 tw-min-h-6 tw-max-w-6 tw-max-h-6"
                                            />
                                            <span className="tw-text-[#232323] tw-text-sm tw-font-normal">
                                              {item.phone}
                                            </span>
                                          </a>
                                        </div>
                                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                                          <a
                                            href={`mailto:${item.email}`}
                                            className="tw-flex tw-items-center tw-gap-2"
                                          >
                                            <img
                                              src={EmailIcon}
                                              alt=""
                                              className="tw-w-6 tw-h-6 tw-min-w-6 tw-min-h-6 tw-max-w-6 tw-max-h-6"
                                            />
                                            <span className="tw-text-[#232323] tw-text-sm tw-font-normal">
                                              {item.email}
                                            </span>
                                          </a>
                                        </div>
                                      </div>
                                    </div>
                                    <div
                                      onClick={() => handleIndividualChat(item)}
                                      className="tw-cursor-pointer"
                                    >
                                      {isIndividualChatCreationLoading[
                                        item.id
                                      ] ? (
                                        <CustomLoader
                                          loading={true}
                                          isWrapper={true}
                                          size="20"
                                        />
                                      ) : (
                                        <img
                                          src={LiveChatIcon}
                                          alt="icon"
                                          className="tw-w-6 tw-h-6 tw-min-w-6 tw-min-h-6 tw-max-w-6 tw-max-h-6"
                                        />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {tier.items.length === 0 && (
                              <div className="tw-text-gray-500 tw-text-center tw-py-4">
                                No contacts available
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="tw-col-span-full tw-text-center tw-py-4">
                      No contacts match your search
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {isViewTermsSheetModalOpen && (
          <ViewTermsSheetModal
            onClose={handleCloseTermsSheet} // Correct modal close handling
          />
        )}

        {isRejectionModalOpen && (
          <TierConfigurationRejectionReason
            onClose={handleCloseRejectionModal}
            onSubmit={handleRejectionSubmit}
          />
        )}

        {individualChatUserId && individualChatRoomId && (
          <StandaloneChatDrawer
            isChatNotificationOpen={isIndividualChatOpen}
            setIsChatNotificationOpen={setIsIndividualChatOpen}
            userId={individualChatUserId}
            customChatRoomId={individualChatRoomId}
            type={StandaloneChatDrawerType.USER}
          />
        )}
      </div>
    </div>
  );
};

export default TierConfigurationModule;
